import React from 'react';
import { Row, Col, Statistic, Skeleton, Empty, Alert } from 'antd';
import {
  SwapOutlined,
  RiseOutlined,
  TeamOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsDealsQuery,
  useGetAnalyticsUsersQuery,
  useGetAnalyticsEngagementQuery,
} from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

function Panel({ title, children, loading }) {
  return (
    <div style={{ ...adminPanelStyle, padding: 18, height: '100%' }}>
      <div
        style={{
          fontFamily: 'var(--df-font-display)',
          fontWeight: 700,
          fontSize: 15,
          color: 'var(--df-text)',
          marginBottom: 12,
        }}
      >
        {title}
      </div>
      {loading ? <Skeleton active paragraph={{ rows: 6 }} /> : children}
    </div>
  );
}

export default function AnalyticsPage() {
  const { data: overview, isLoading: overviewLoading, isError, error } = useGetAnalyticsOverviewQuery();
  const { data: dealData, isLoading: dealsLoading } = useGetAnalyticsDealsQuery();
  const { data: userData, isLoading: usersLoading } = useGetAnalyticsUsersQuery();
  const { data: engagementData, isLoading: engagementLoading } = useGetAnalyticsEngagementQuery();

  const engagementChart = (engagementData?.votes || []).map((v) => ({
    date: v.date,
    Votes: v.count,
    Comments: (engagementData.comments || []).find((c) => c.date === v.date)?.count || 0,
    Saves: (engagementData.saves || []).find((s) => s.date === v.date)?.count || 0,
  }));

  return (
    <div>
      <AdminPageHeader
        title="Analytics"
        subtitle="Deal volume, signups, and engagement over the last 30 days"
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load analytics'}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { title: 'Total Deals', value: overview?.totalDeals, icon: <SwapOutlined />, color: 'var(--df-primary)' },
          { title: 'Active Users', value: overview?.activeUsers, icon: <TeamOutlined />, color: 'var(--df-info)' },
          { title: 'Total Votes', value: overview?.totalVotes, icon: <RiseOutlined />, color: 'var(--df-success)' },
          { title: 'Total Comments', value: overview?.totalComments, icon: <MessageOutlined />, color: 'var(--df-primary-muted)' },
        ].map((card) => (
          <Col xs={24} sm={12} lg={6} key={card.title}>
            <div style={{ ...adminPanelStyle, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ color: card.color, fontSize: 18 }}>{card.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-text-secondary)' }}>{card.title}</span>
              </div>
              <Statistic
                value={overviewLoading ? '—' : (card.value || 0)}
                valueStyle={{
                  fontSize: 26,
                  fontWeight: 700,
                  fontFamily: 'var(--df-font-display)',
                  color: 'var(--df-text)',
                }}
              />
            </div>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Panel title="Deal Submissions (Last 30 Days)" loading={dealsLoading}>
            {dealData?.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dealData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--df-border-subtle)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#0B3A5C" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty description="No deal submission data" />}
          </Panel>
        </Col>
        <Col xs={24} lg={12}>
          <Panel title="User Signups (Last 30 Days)" loading={usersLoading}>
            {userData?.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={userData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--df-border-subtle)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#0F7B5A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <Empty description="No signup data" />}
          </Panel>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Panel title="Engagement (Last 30 Days)" loading={engagementLoading}>
            {engagementChart.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={engagementChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--df-border-subtle)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="Votes" fill="#0B3A5C" />
                  <Bar dataKey="Comments" fill="#0F7B5A" />
                  <Bar dataKey="Saves" fill="#E85D04" />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty description="No engagement data" />}
          </Panel>
        </Col>
      </Row>
    </div>
  );
}
